import { useEffect, useState, useCallback } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { useNavigate, useParams, useLocation } from 'react-router';
import { ConfirmDialog, Pagination } from '../../components';
import {
  staffActivitiesService,
  partnerActivitiesService,
  staffParticipantsService,
  partnerParticipantsService,
  STAFF_PAGE_SIZE,
} from '../../lib/api-service';
import { getToken, BACKEND_URL, apiFetch } from '../../lib/api';
import type { ActivityDetail, Participant, PaginatedResponse } from '../../lib/types';
import { formatDate, formatTime } from '../../utils/date';
import {
  ArrowLeft,
  Loader2,
  Printer,
  Download,
  Search,
  XCircle,
  CheckCircle2,
  Users,
} from 'lucide-react';

export default function ActivityParticipants() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPartnerView = location.pathname.startsWith('/staff/activitatile-mele');

  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, confirmed: 0, cancelled: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelTarget, setCancelTarget] = useState<Participant | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [participantTypeFilter, setParticipantTypeFilter] = useState('');

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const params: { page: number; search?: string; status?: string; participant_type?: string } = { page };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (participantTypeFilter) params.participant_type = participantTypeFilter;
      const [activityData, participantData] = await Promise.all([
        isPartnerView
          ? partnerActivitiesService.detail(slug)
          : staffActivitiesService.detail(slug),
        isPartnerView
          ? partnerParticipantsService.list(slug, { page })
          : staffParticipantsService.list(slug, params),
      ]);
      setActivity(activityData);
      setParticipants(participantData.results);
      setTotalCount(participantData.count);
      if (participantData.stats) setStats(participantData.stats);
    } catch {
      setError('Nu s-au putut încărca datele.');
    } finally {
      setLoading(false);
    }
  }, [slug, page, isPartnerView, searchQuery, statusFilter, participantTypeFilter]);

  useEffect(() => { load(); }, [load]);

  async function confirmCancel() {
    if (!cancelTarget || !slug) return;
    try {
      await staffParticipantsService.cancel(slug, cancelTarget.id);
      setCancelTarget(null);
      await load();
    } catch {
      setError('Eroare la anularea înregistrării.');
    }
  }

  function buildFilterQs() {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter) params.set('status', statusFilter);
    if (participantTypeFilter) params.set('participant_type', participantTypeFilter);
    return params.toString();
  }

  async function handlePrint() {
    if (!slug) return;
    const path = isPartnerView
      ? partnerParticipantsService.printUrl(slug)
      : staffParticipantsService.printUrl(slug);
    const filterQs = buildFilterQs();
    try {
      const token = getToken();
      const res = await fetch(`${BACKEND_URL}/api/v1${path}${filterQs ? '&' + filterQs : ''}`, {
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load print view');
      const html = await res.text();
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
        win.print();
      }
    } catch {
      setError('Eroare la generarea listei de printare.');
    }
  }

  async function exportCSV() {
    if (!activity || !slug) return;
    // Fetch all participants for CSV
    const basePath = isPartnerView
      ? `/staff/partner-activities/${slug}/participants/`
      : `/staff/activities/${slug}/participants/`;
    const filterQs = buildFilterQs();
    const allData = await apiFetch<PaginatedResponse<Participant>>(`${basePath}?page_size=10000${filterQs ? '&' + filterQs : ''}`);
    const all = allData.results;
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const headers = ['Nume', 'Prenume', 'Email', 'Telefon', 'Tip', 'Vârstă', 'Status', 'Data înregistrării'];
    const rows = all.map((p) => [
      escape(p.last_name),
      escape(p.first_name),
      escape(p.email),
      escape(p.phone || ''),
      escape(p.participant_type?.title || ''),
      p.age ?? '',
      escape(p.status),
      escape(formatDate(p.registered_at)),
    ]);
    const csv = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = formatDate(new Date().toISOString()).replace(/\//g, '-');
    link.href = blobUrl;
    link.download = `participanti_${activity.slug}_${today}.csv`;
    link.click();
    URL.revokeObjectURL(blobUrl);
  }

  const backPath = isPartnerView ? '/staff/activitatile-mele' : '/staff/activitati';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  const isRedacted = participants.length > 0 && participants[0].first_name === '••••';

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate(backPath)}
          className="rounded-lg p-2 text-muted-foreground hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              Participanți
            </h1>
            <HelpButton to="/staff/documentatie/participanti" />
          </div>
          {activity && (
            <p className="mt-0.5 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              {activity.title} — {formatDate(activity.start_datetime)}, {formatTime(activity.start_datetime)}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {!isRedacted && (
            <>
              <button
                onClick={handlePrint}
                disabled={participants.length === 0}
                className="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                title="Printează lista"
              >
                <Printer className="h-4 w-4" />
                <span className="hidden sm:inline">Printează</span>
              </button>
              <button
                onClick={exportCSV}
                disabled={participants.length === 0}
                className="flex items-center gap-2 rounded-lg border border-[var(--vivid-purple)] px-3 py-2 text-sm font-semibold text-[var(--vivid-purple)] transition-colors hover:bg-[var(--vivid-purple)]/5 disabled:opacity-40"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export CSV</span>
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {isRedacted && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <strong>Datele participanților sunt ascunse.</strong> Completează datele legale și semnează Acordul de Prelucrare a Datelor (DPA) pentru a avea acces la informațiile complete.
        </div>
      )}

      {/* Stats */}
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5">
          <Users className="h-4 w-4 text-[var(--vivid-purple)]" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            {stats.total} total
          </span>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-semibold text-green-700" style={{ fontFamily: 'var(--font-primary)' }}>
            {stats.confirmed} confirmați
          </span>
        </div>
        {stats.cancelled > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2.5">
            <XCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm font-semibold text-red-600" style={{ fontFamily: 'var(--font-primary)' }}>
              {stats.cancelled} anulați
            </span>
          </div>
        )}
        {activity?.participant_limit && (
          <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-2.5">
            <span className="text-sm font-semibold text-blue-700" style={{ fontFamily: 'var(--font-primary)' }}>
              {stats.confirmed} / {activity.participant_limit} locuri
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      {!isRedacted && (
        <div className="mb-4 flex flex-wrap gap-3">
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearchQuery(searchInput); }} className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Caută după nume sau email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-lg border bg-white py-2 pl-10 pr-4 text-sm"
              style={{ fontFamily: 'var(--font-secondary)' }}
            />
          </form>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border bg-white px-3 py-2 text-sm"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            <option value="">Toate statusurile</option>
            <option value="confirmed">Confirmat</option>
            <option value="cancelled">Anulat</option>
          </select>
          {activity && activity.participant_types && activity.participant_types.length > 1 && (
            <select
              value={participantTypeFilter}
              onChange={(e) => { setParticipantTypeFilter(e.target.value); setPage(1); }}
              className="rounded-lg border bg-white px-3 py-2 text-sm"
              style={{ fontFamily: 'var(--font-secondary)' }}
            >
              <option value="">Toate tipurile</option>
              {activity.participant_types.map((t) => (
                <option key={t.id} value={String(t.id)}>{t.title}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Table */}
      {participants.length === 0 ? (
        <div className="rounded-xl border bg-white py-16 text-center shadow-sm">
          <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Nu există participanți înregistrați.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/50">
                  <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Nume</th>
                  <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Email</th>
                  <th className="hidden px-4 py-3 text-left font-semibold sm:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Telefon</th>
                  <th className="hidden px-4 py-3 text-left font-semibold md:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Vârstă</th>
                  <th className="hidden px-4 py-3 text-left font-semibold lg:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Tip</th>
                  <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Status</th>
                  <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Data</th>
                  {!isPartnerView && (
                    <th className="px-4 py-3 text-right font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Acțiuni</th>
                  )}
                </tr>
              </thead>
              <tbody style={{ fontFamily: 'var(--font-secondary)' }}>
                {participants.map((p) => {
                  const isConfirmed = p.status === 'confirmed' || p.status === 'Confirmat';
                  return (
                    <tr key={p.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                      <td className={`px-4 py-3 font-medium ${isRedacted ? 'select-none text-muted-foreground/40' : ''}`}>
                        {p.first_name} {p.last_name}
                      </td>
                      <td className={`px-4 py-3 ${isRedacted ? 'select-none text-muted-foreground/40' : 'text-muted-foreground'}`}>{p.email}</td>
                      <td className={`hidden px-4 py-3 sm:table-cell ${isRedacted ? 'select-none text-muted-foreground/40' : 'text-muted-foreground'}`}>{p.phone || '—'}</td>
                      <td className={`hidden px-4 py-3 md:table-cell ${isRedacted ? 'select-none text-muted-foreground/40' : 'text-muted-foreground'}`}>{p.age ?? '—'}</td>
                      <td className="hidden px-4 py-3 lg:table-cell">
                        {p.participant_type ? (
                          <span className="text-xs">
                            {p.participant_type.title}
                            {parseFloat(p.participant_type.price) > 0 && (
                              <span className="ml-1 text-muted-foreground">({parseFloat(p.participant_type.price)} RON)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isConfirmed ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                            <CheckCircle2 className="h-3 w-3" /> Confirmat
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-600">
                            <XCircle className="h-3 w-3" /> Anulat
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {formatDate(p.registered_at)}
                      </td>
                      {!isPartnerView && (
                        <td className="px-4 py-3 text-right">
                          {isConfirmed && (
                            <button
                              onClick={() => setCancelTarget(p)}
                              className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                            >
                              Anulează
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      {/* Cancel confirmation */}
      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        onConfirm={confirmCancel}
        title="Anulează înregistrarea"
        description={`Sigur vrei să anulezi înregistrarea lui ${cancelTarget?.first_name} ${cancelTarget?.last_name}?`}
      />
    </div>
  );
}
